import { motion } from "framer-motion";

const VALUE_PROPS = [
  {
    number: "01",
    title: "NOT AN ASSISTANT. AN INTELLIGENCE LAYER.",
    body: "MERAI doesn't bolt onto your existing workflow — it replaces the bottlenecks. Every design decision, product presentation, and content brief runs through AI-native intelligence built for how retail actually operates. The result: fewer corrections, faster timelines, and a team that can focus on what humans do best.",
  },
  {
    number: "02",
    title: "THE ECONOMICS OF RETAIL CHANGE COMPLETELY.",
    body: "When you stop paying for photoshoots, eliminate planogram errors before they hit the floor, and cut design correction cycles by 65%, your bottomline notices. MERAI's ROI is measurable, immediate, and compounds over time. This isn't productivity software — it's a financial transformation engine.",
  },
  {
    number: "03",
    title: "FROM SKETCH TO STOREFRONT. ONE PLATFORM.",
    body: "Virtual try-on to techpacking. GenAI content to merchandising intelligence. MERAI covers the full product journey so your team operates with a single source of intelligence — not a patchwork of disconnected tools. Four products. One layer. Infinite scale.",
  },
];

const WhyMerai = () => (
  <section
    id="why-merai"
    data-testid="why-merai-section"
    className="bg-[#0A0A0A] py-24 md:py-40"
  >
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      {/* Overline */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-white/30 mb-16"
      >
        The MERAI Advantage
      </motion.p>

      <div className="md:flex md:gap-24">
        {/* Left — Sticky heading */}
        <div className="md:w-5/12 md:sticky md:top-28 md:self-start mb-16 md:mb-0">
          <motion.h2
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
            data-testid="why-merai-heading"
            className="font-cabinet text-5xl sm:text-6xl md:text-7xl font-black uppercase text-[#F4F4F0] leading-none tracking-tighter"
          >
            WHY
            <br />
            <span className="text-[#00FF66]">MERAI</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="font-manrope text-base text-white/50 leading-relaxed mt-6 max-w-xs"
          >
            MERAI means future in Japanese. We're not building the future of
            retail — we're deploying it, right now.
          </motion.p>
        </div>

        {/* Right — Value props */}
        <div className="md:w-7/12 flex flex-col">
          {VALUE_PROPS.map((prop, i) => (
            <motion.div
              key={prop.number}
              data-testid={`why-merai-prop-${prop.number}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true, margin: "-40px" }}
              className="border-t border-white/10 py-12"
            >
              <span className="font-mono text-[#00FF66] text-sm font-bold tracking-widest block mb-4">
                {prop.number}
              </span>
              <h3 className="font-cabinet text-xl md:text-2xl font-black uppercase text-[#F4F4F0] leading-tight mb-4">
                {prop.title}
              </h3>
              <p className="font-manrope text-base text-white/55 leading-relaxed">
                {prop.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default WhyMerai;
